/* ReviewForm — dialog buat/edit ulasan untuk item pesanan yang sudah selesai. */
import React, { useState } from "react";
import { Star, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { imageUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MAX_IMG = 5, IMG_MB = 3, VID_MB = 20;

export default function ReviewForm({ open, onOpenChange, orderId, item, existing, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [keepImgs, setKeepImgs] = useState(existing?.media?.images || []);
  const [newImgs, setNewImgs] = useState([]);
  const [video, setVideo] = useState(null);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hasOldVideo = !!existing?.media?.video && !removeVideo;

  const addImages = (files) => {
    const list = Array.from(files || []);
    if (keepImgs.length + newImgs.length + list.length > MAX_IMG) return setError(`Maksimal ${MAX_IMG} gambar.`);
    for (const f of list) {
      if (!/\.(jpe?g|png|webp)$/i.test(f.name)) return setError("Gambar harus JPG, PNG, atau WEBP.");
      if (f.size > IMG_MB * 1024 * 1024) return setError(`Ukuran gambar maksimal ${IMG_MB} MB.`);
    }
    setError(""); setNewImgs((s) => [...s, ...list]);
  };
  const pickVideo = (f) => {
    if (!f) return;
    if (!/\.mp4$/i.test(f.name)) return setError("Video harus MP4.");
    if (f.size > VID_MB * 1024 * 1024) return setError(`Ukuran video maksimal ${VID_MB} MB.`);
    setError(""); setVideo(f); setRemoveVideo(true);
  };

  const submit = async () => {
    if (rating < 1) return setError("Pilih rating 1–5 bintang.");
    setBusy(true); setError("");
    const fd = new FormData();
    fd.append("rating", String(rating)); fd.append("comment", comment);
    newImgs.forEach((f) => fd.append("images", f));
    if (video) fd.append("video", video);
    try {
      if (existing) {
        fd.append("keep_image_keys", JSON.stringify(keepImgs.map((m) => m.key)));
        fd.append("remove_video", String(removeVideo && !video));
        await api.put(`/reviews/${existing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        fd.append("order_id", orderId); fd.append("order_item_id", item.order_item_id);
        await api.post("/reviews", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(existing ? "Ulasan diperbarui" : "Terima kasih atas ulasan Anda!");
      onSaved?.(); onOpenChange(false);
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="review-form">
        <DialogHeader><DialogTitle>{existing ? "Edit Ulasan" : "Beri Ulasan"} — {item?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-1" data-testid="review-form-rating">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setRating(i)} aria-label={`${i} bintang`} data-testid={`review-star-${i}`}>
                <Star size={28} className={i <= rating ? "text-[#F4A261] fill-[#F4A261]" : "text-[#E5E5E5]"} />
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 2000))} placeholder="Ceritakan pengalaman Anda (opsional)" rows={4} data-testid="review-form-comment" />
          <div>
            <div className="text-xs text-[#5C5C5C] mb-1">Foto (maks {MAX_IMG}, JPG/PNG/WEBP, maks {IMG_MB} MB) · Video (maks 1, MP4, maks {VID_MB} MB)</div>
            <div className="flex gap-2 flex-wrap">
              {keepImgs.map((m) => (
                <div key={m.key} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                  <img src={imageUrl(m.url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setKeepImgs((s) => s.filter((x) => x.key !== m.key))} className="absolute top-0 right-0 bg-white/90 rounded-bl p-0.5" aria-label="Hapus foto"><X size={12} /></button>
                </div>
              ))}
              {newImgs.map((f, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setNewImgs((s) => s.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-white/90 rounded-bl p-0.5" aria-label="Hapus foto"><X size={12} /></button>
                </div>
              ))}
              {keepImgs.length + newImgs.length < MAX_IMG && (
                <label className="w-16 h-16 rounded-lg border-2 border-dashed grid place-items-center text-xs cursor-pointer">+ Foto
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => addImages(e.target.files)} data-testid="review-form-images" />
                </label>
              )}
            </div>
            <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
              {video ? <span data-testid="review-form-video-name">{video.name}</span> : hasOldVideo ? <span>Video saat ini tersimpan</span> : null}
              {(video || hasOldVideo) && (
                <button type="button" className="underline" onClick={() => { setVideo(null); setRemoveVideo(true); }} data-testid="review-form-video-remove">Hapus video</button>
              )}
              {!video && !hasOldVideo && (
                <label className="underline cursor-pointer">+ Video MP4
                  <input type="file" accept="video/mp4,.mp4" hidden onChange={(e) => pickVideo(e.target.files?.[0])} data-testid="review-form-video" />
                </label>
              )}
            </div>
          </div>
          {error && <div className="text-xs text-[#DC2F02]" data-testid="review-form-error">{error}</div>}
          <Button onClick={submit} disabled={busy} className="w-full rounded-xl" data-testid="review-form-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : existing ? "Simpan Perubahan" : "Kirim Ulasan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
